/**
 * EnemyProfileDialog -- View another player's public profile & fleet.
 *
 * Two views:
 *   1. Main view  -- profile summary + fleet list
 *   2. Ship detail -- stats, components, crew for a single ship
 *
 * Data fetched from /api/profile/player/:userId and /api/profile/player/:userId/ships.
 * Opens via EventBus 'open-enemy-profile' with { userId: string }.
 */

import Phaser from 'phaser';
import {
  getResponsiveDialogSize,
  getResponsiveFontSize,
  createScrollableContainer,
  isMobile,
} from '../../utils/ResponsiveHelper';
import { I18n } from '../../i18n/I18nService';
import { EventBus } from '../../utils/EventBus';

// ---- Colour & layout constants (matches ProfileDialog) ----

const DESKTOP_WIDTH = 560;
const DESKTOP_HEIGHT = 620;
const BG_COLOR = 0x0f1a2e;
const BORDER_COLOR = 0x3366aa;
const HEADER_COLOR = 0x1a2a4e;
const CARD_COLOR = 0x162040;
const CARD_HOVER_COLOR = 0x1e2e55;
const ACCENT_COLOR = 0x44aaff;
const SECTION_HEADER_COLOR = 0x192844;

// ---- Response interfaces ----

interface PirateFactionInfo {
  factionId: string;
  factionName: string;
  starbasePosition: { q: number; r: number } | null;
  starbaseHp: number | null;
  starbaseMaxHp: number | null;
  isDestroyed: boolean | null;
}

interface PublicProfile {
  userId: string;
  displayName: string;
  shipCount: number;
  alliance: { id: string; name: string; tag: string } | null;
  createdAt: string | null;
  isPirate?: boolean;
  pirateFaction?: PirateFactionInfo;
}

interface ShipSummary {
  id: string;
  unitType: string;
  currentHp: number;
  maxHp: number;
  shields: number;
  maxShields: number;
  position: { q: number; r: number };
  createdAt: string;
  crew: { id: string; name: string; level: number } | null;
}

interface ComponentInfo {
  id: string;
  name: string;
  category: string;
  currentTier: number;
  stats: Record<string, number> | null;
  description: string;
}

interface ShipDetail {
  ship: {
    id: string;
    unitType: string;
    currentHp: number;
    maxHp: number;
    shields: number;
    maxShields: number;
    position: { q: number; r: number };
    createdAt: string;
  };
  components: ComponentInfo[];
  crew: any | null;
}

// ---- Display-name helpers ----

function unitDisplayName(type: string): string {
  return I18n.shipName(type);
}

// ====================================================================
// EnemyProfileDialog
// ====================================================================

export class EnemyProfileDialog extends Phaser.GameObjects.Container {
  private targetUserId: string;
  private profile: PublicProfile | null = null;
  private ships: ShipSummary[] = [];
  private shipDetail: ShipDetail | null = null;
  private dims: ReturnType<typeof getResponsiveDialogSize>;

  private scrollInner: Phaser.GameObjects.Container | null = null;
  private scrollUpdate: (() => void) | null = null;
  private statusText: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene, userId: string) {
    const dims = getResponsiveDialogSize(DESKTOP_WIDTH, DESKTOP_HEIGHT, scene);
    super(scene, dims.x, dims.y);
    this.dims = dims;
    this.targetUserId = userId;

    const { width, height } = scene.cameras.main;
    const dw = dims.width;
    const dh = dims.height;

    // Dark overlay
    const overlay = scene.add.rectangle(-dims.x, -dims.y, width, height, 0x000000, 0.7);
    overlay.setInteractive();
    overlay.on('pointerdown', () => this.close());
    this.add(overlay);

    // Main panel
    const bg = scene.add.rectangle(0, 0, dw, dh, BG_COLOR, 1);
    bg.setOrigin(0, 0);
    bg.setInteractive();
    this.add(bg);

    // Border
    const border = scene.add.graphics();
    border.lineStyle(2, BORDER_COLOR, 1);
    border.strokeRect(0, 0, dw, dh);
    this.add(border);

    // Header bar
    const headerBg = scene.add.rectangle(0, 0, dw, 50, HEADER_COLOR, 1);
    headerBg.setOrigin(0, 0);
    this.add(headerBg);

    const isPirate = userId.startsWith('AI_PIRATE_');
    const titleLabel = isPirate ? I18n.t('dialog.pirate_faction') : I18n.t('dialog.player_profile');
    const titleColor = isPirate ? '#ff4444' : '#44aaff';
    const title = scene.add.text(dw / 2, 25, titleLabel, {
      fontSize: `${getResponsiveFontSize(20, scene)}px`,
      color: titleColor,
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    this.add(title);

    // Close button — hugged to the top-right corner, mobile-sized hit area.
    this.add(this.createCloseButton(dw));

    // Scrollable content area
    const listTop = 55;
    const listBottom = 25;
    const listHeight = dh - listTop - listBottom;
    const { container, updateScrollBounds } = createScrollableContainer(scene, this, {
      x: 0, y: listTop, width: dw, height: listHeight,
    });
    this.scrollInner = container;
    this.scrollUpdate = updateScrollBounds;

    // Status text
    this.statusText = scene.add.text(dw / 2, dh - 18, '', {
      fontSize: `${getResponsiveFontSize(12, scene)}px`,
      color: '#889999',
      fontFamily: 'Share Tech Mono, monospace',
    });
    this.statusText.setOrigin(0.5);
    this.add(this.statusText);

    this.setDepth(2000);
    scene.add.existing(this);
    this.loadProfile();
  }

  // ------------------------------------------------------------------ helpers

  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('crimsonmandate_token');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }

  private setStatus(msg: string): void { this.statusText.setText(msg); }

  // ------------------------------------------------------------------ close

  private createCloseButton(dw: number): Phaser.GameObjects.Container {
    // Responsive sizing — larger hit target on mobile (44x44+ recommended for
    // touch), trim to a tighter visual on desktop.
    //
    // IMPORTANT: use TOP-LEFT origin for every child + the hit rectangle so
    // that the visible button and its collider are literally the same box.
    // Phaser Container.setInteractive anchors the hit Rectangle at the
    // container's local (0,0) — mixing that with center-origin children
    // causes the collider to drift above the visible button (the user had to
    // tap the upper edge for the X to register).
    const mobile = isMobile(this.scene);
    const SIZE = mobile ? 44 : 36;
    const FONT = mobile ? 22 : 18;
    const PAD  = 6;   // gap from dialog corner
    // Top-left of the button, in dialog-local coords.
    const bx = dw - PAD - SIZE;
    const by = PAD;

    const button = this.scene.add.container(bx, by);
    // Visible background — anchored at container-local (0,0) using top-left origin.
    const bg = this.scene.add.rectangle(0, 0, SIZE, SIZE, 0x660000);
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(2, 0xff4444);
    button.add(bg);
    // Label centered inside the bg.
    const text = this.scene.add.text(SIZE / 2, SIZE / 2, 'X', {
      fontSize: `${FONT}px`, color: '#ffffff', fontFamily: 'Arial', fontStyle: 'bold',
    });
    text.setOrigin(0.5);
    button.add(text);
    button.setSize(SIZE, SIZE);
    // Hit area = visible bg (both at container-local (0,0) with same SIZE).
    button.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, SIZE, SIZE),
      Phaser.Geom.Rectangle.Contains,
    );
    button.on('pointerover', () => bg.setFillStyle(0x880000));
    button.on('pointerout', () => bg.setFillStyle(0x660000));
    button.on('pointerdown', () => this.close());
    return button;
  }

  private close(): void { this.destroy(); }

  // ================================================================== Data fetching

  private async loadProfile(): Promise<void> {
    this.setStatus('Loading profile...');
    try {
      const [profileRes, shipsRes] = await Promise.all([
        fetch(`/api/profile/player/${this.targetUserId}`, { headers: this.getAuthHeaders() }),
        fetch(`/api/profile/player/${this.targetUserId}/ships`, { headers: this.getAuthHeaders() }),
      ]);
      const profileJson = await profileRes.json();
      const shipsJson = await shipsRes.json();

      if (!profileJson.success) { this.setStatus('Failed to load profile.'); return; }
      this.profile = profileJson.data as PublicProfile;
      this.ships = shipsJson.success ? (shipsJson.data.ships as ShipSummary[]) : [];
      this.setStatus('');
      this.renderMainView();
    } catch (err) {
      console.error('[EnemyProfileDialog] loadProfile error:', err);
      this.setStatus('Network error. Try again later.');
    }
  }

  private async loadShipDetail(shipId: string): Promise<void> {
    this.setStatus('Loading ship detail...');
    try {
      const res = await fetch(`/api/profile/player/${this.targetUserId}/ships/${shipId}`, {
        headers: this.getAuthHeaders(),
      });
      const json = await res.json();
      if (!json.success) { this.setStatus('Failed to load ship detail.'); return; }
      this.shipDetail = json.data as ShipDetail;
      this.setStatus('');
      this.renderDetailView();
    } catch (err) {
      console.error('[EnemyProfileDialog] loadShipDetail error:', err);
      this.setStatus('Network error. Try again later.');
    }
  }

  // ================================================================== Main View

  private renderMainView(): void {
    if (!this.scrollInner) return;
    this.scrollInner.removeAll(true);
    if (!this.profile) return;

    const p = this.profile;
    const dw = this.dims.width;
    let yOffset = 10;

    // Profile summary
    const isPirate = !!p.isPirate;
    const summaryHeight = isPirate ? 110 : 80;
    const summaryBg = this.scene.add.rectangle(10, yOffset, dw - 20, summaryHeight, 0x111e33, 1);
    summaryBg.setOrigin(0, 0);
    summaryBg.setStrokeStyle(1, isPirate ? 0x993333 : BORDER_COLOR, 0.4);
    this.scrollInner.add(summaryBg);

    const nameLabel = isPirate ? '' : 'Commander: ';
    const nameColor = isPirate ? '#ff6666' : '#ffffff';
    const nameText = this.scene.add.text(20, yOffset + 12, `${nameLabel}${p.displayName || 'Unknown'}`, {
      fontSize: `${getResponsiveFontSize(16, this.scene)}px`,
      color: nameColor,
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    this.scrollInner.add(nameText);

    if (isPirate) {
      const factionBadge = this.scene.add.text(20, yOffset + 36, 'NPC Faction', {
        fontSize: `${getResponsiveFontSize(13, this.scene)}px`,
        color: '#cc8844',
        fontFamily: 'Share Tech Mono, monospace',
      });
      this.scrollInner.add(factionBadge);

      const pf = p.pirateFaction!;
      const sbStatus = pf.isDestroyed ? 'DESTROYED' : `HP: ${pf.starbaseHp}/${pf.starbaseMaxHp}`;
      const sbColor = pf.isDestroyed ? '#ff4444' : '#88cc88';
      const sbPos = pf.starbasePosition ? `(${pf.starbasePosition.q}, ${pf.starbasePosition.r})` : 'Unknown';
      const starbaseText = this.scene.add.text(20, yOffset + 56, `Starbase: ${sbStatus}   |   Position: ${sbPos}`, {
        fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
        color: sbColor,
        fontFamily: 'Share Tech Mono, monospace',
      });
      this.scrollInner.add(starbaseText);

      const fleetStr = `Fleet size: ${p.shipCount}`;
      const fleetText = this.scene.add.text(20, yOffset + 76, fleetStr, {
        fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
        color: '#aabbcc',
        fontFamily: 'Share Tech Mono, monospace',
      });
      this.scrollInner.add(fleetText);
    } else {
      const allianceStr = p.alliance ? `${p.alliance.name} [${p.alliance.tag}]` : 'None';
      const allianceText = this.scene.add.text(20, yOffset + 36, `Alliance: ${allianceStr}`, {
        fontSize: `${getResponsiveFontSize(13, this.scene)}px`,
        color: '#8899bb',
        fontFamily: 'Share Tech Mono, monospace',
      });
      this.scrollInner.add(allianceText);

      const joinDate = new Date(p.createdAt!).toLocaleDateString();
      const statsStr = `Ships: ${p.shipCount}   |   Joined: ${joinDate}`;
      const statsText = this.scene.add.text(20, yOffset + 56, statsStr, {
        fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
        color: '#aabbcc',
        fontFamily: 'Share Tech Mono, monospace',
      });
      this.scrollInner.add(statsText);
    }

    // Send Message button (not for pirate NPCs)
    if (!isPirate) {
      const msgBtn = this.createSmallButton(dw - 140, yOffset + 12, 120, 28, 'SEND MESSAGE', () => {
        EventBus.emit('mmo-open-dm', {
          recipientId: p.userId,
          recipientName: p.displayName || p.userId,
        });
        this.close();
      });
      this.scrollInner.add(msgBtn);
    }

    yOffset += summaryHeight + 15;

    // Fleet header
    const fleetHeaderBg = this.scene.add.rectangle(10, yOffset, dw - 20, 30, SECTION_HEADER_COLOR, 1);
    fleetHeaderBg.setOrigin(0, 0);
    this.scrollInner.add(fleetHeaderBg);

    const fleetHeader = this.scene.add.text(dw / 2, yOffset + 15, 'FLEET', {
      fontSize: `${getResponsiveFontSize(14, this.scene)}px`,
      color: '#44aaff',
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    fleetHeader.setOrigin(0.5);
    this.scrollInner.add(fleetHeader);
    yOffset += 38;

    // Ship cards
    if (this.ships.length === 0) {
      const emptyText = this.scene.add.text(dw / 2, yOffset + 40, 'No ships in fleet.', {
        fontSize: `${getResponsiveFontSize(13, this.scene)}px`,
        color: '#556677',
        fontFamily: 'Share Tech Mono, monospace',
        align: 'center',
      });
      emptyText.setOrigin(0.5, 0);
      this.scrollInner.add(emptyText);
    } else {
      const cardWidth = dw - 30;
      const cardHeight = 44;
      for (const ship of this.ships) {
        const card = this.createShipCard(15, yOffset, cardWidth, cardHeight, ship);
        this.scrollInner.add(card);
        yOffset += cardHeight + 4;
      }
    }
    this.scrollUpdate?.();
  }

  private createShipCard(x: number, y: number, w: number, h: number, ship: ShipSummary): Phaser.GameObjects.Container {
    const card = this.scene.add.container(x, y);
    const bg = this.scene.add.rectangle(0, 0, w, h, CARD_COLOR);
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(1, 0x334466);
    card.add(bg);

    const typeName = unitDisplayName(ship.unitType);
    const typeText = this.scene.add.text(10, h / 2 - 8, typeName, {
      fontSize: `${getResponsiveFontSize(14, this.scene)}px`,
      color: '#ccddff',
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    typeText.setOrigin(0, 0.5);
    card.add(typeText);

    const midX = this.dims.isMobile ? w * 0.4 : 150;

    const hpStr = `HP: ${ship.currentHp}/${ship.maxHp}`;
    const hpText = this.scene.add.text(midX, h / 2 - 8, hpStr, {
      fontSize: `${getResponsiveFontSize(11, this.scene)}px`,
      color: '#88cc88',
      fontFamily: 'Share Tech Mono, monospace',
    });
    hpText.setOrigin(0, 0.5);
    card.add(hpText);

    const crewStr = ship.crew ? `Crew: ${ship.crew.name} Lv.${ship.crew.level}` : 'Crew: None';
    const crewColor = ship.crew ? '#aabbcc' : '#556677';
    const crewText = this.scene.add.text(midX, h / 2 + 8, crewStr, {
      fontSize: `${getResponsiveFontSize(10, this.scene)}px`,
      color: crewColor,
      fontFamily: 'Share Tech Mono, monospace',
    });
    crewText.setOrigin(0, 0.5);
    card.add(crewText);

    // VIEW button
    const viewBtn = this.createSmallButton(w - 55, h / 2 - 11, 45, 22, 'VIEW', () => {
      this.loadShipDetail(ship.id);
    });
    card.add(viewBtn);

    card.setSize(w, h);
    card.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    card.on('pointerover', () => bg.setFillStyle(CARD_HOVER_COLOR));
    card.on('pointerout', () => bg.setFillStyle(CARD_COLOR));
    return card;
  }

  // ================================================================== Detail View

  private renderDetailView(): void {
    if (!this.scrollInner) return;
    this.scrollInner.removeAll(true);
    if (!this.shipDetail) return;

    const { ship, components, crew } = this.shipDetail;
    const dw = this.dims.width;
    let yOffset = 8;

    // BACK button
    const backBtn = this.createSmallButton(15, yOffset, 60, 24, 'BACK', () => { this.renderMainView(); });
    this.scrollInner.add(backBtn);

    const detailTitle = this.scene.add.text(dw / 2, yOffset + 12, 'SHIP DETAIL', {
      fontSize: `${getResponsiveFontSize(16, this.scene)}px`,
      color: '#44aaff',
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    detailTitle.setOrigin(0.5);
    this.scrollInner.add(detailTitle);
    yOffset += 36;

    // Ship info
    const infoBg = this.scene.add.rectangle(10, yOffset, dw - 20, 70, 0x111e33, 1);
    infoBg.setOrigin(0, 0);
    infoBg.setStrokeStyle(1, BORDER_COLOR, 0.4);
    this.scrollInner.add(infoBg);

    const typeName = unitDisplayName(ship.unitType);
    const line1 = `Type: ${typeName}   |   HP: ${ship.currentHp}/${ship.maxHp}   |   Shields: ${ship.shields}/${ship.maxShields}`;
    const line1Text = this.scene.add.text(20, yOffset + 14, line1, {
      fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
      color: '#ccddee',
      fontFamily: 'Share Tech Mono, monospace',
    });
    this.scrollInner.add(line1Text);

    const posStr = `(${ship.position.q}, ${ship.position.r})`;
    const createdStr = new Date(ship.createdAt).toLocaleDateString();
    const line2 = `Position: ${posStr}   |   Created: ${createdStr}`;
    const line2Text = this.scene.add.text(20, yOffset + 34, line2, {
      fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
      color: '#8899bb',
      fontFamily: 'Share Tech Mono, monospace',
    });
    this.scrollInner.add(line2Text);
    yOffset += 80;

    // Components section
    const compHeaderBg = this.scene.add.rectangle(10, yOffset, dw - 20, 26, SECTION_HEADER_COLOR, 1);
    compHeaderBg.setOrigin(0, 0);
    this.scrollInner.add(compHeaderBg);

    const compHeader = this.scene.add.text(20, yOffset + 13, 'COMPONENTS', {
      fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
      color: '#44aaff',
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    compHeader.setOrigin(0, 0.5);
    this.scrollInner.add(compHeader);
    yOffset += 32;

    if (components.length === 0) {
      const noComp = this.scene.add.text(25, yOffset, '[empty slots]', {
        fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
        color: '#556677',
        fontFamily: 'Share Tech Mono, monospace',
      });
      this.scrollInner.add(noComp);
      yOffset += 20;
    } else {
      for (const comp of components) {
        const statsEntries = comp.stats ? Object.entries(comp.stats) : [];
        const statStr = statsEntries.map(([k, v]) => `${v} ${k}`).join(', ');
        const compLine = `* ${comp.name} Mk${comp.currentTier} (${comp.category})${statStr ? ' - ' + statStr : ''}`;
        const compText = this.scene.add.text(25, yOffset, compLine, {
          fontSize: `${getResponsiveFontSize(11, this.scene)}px`,
          color: '#aabbcc',
          fontFamily: 'Share Tech Mono, monospace',
          wordWrap: { width: dw - 60 },
        });
        this.scrollInner.add(compText);
        yOffset += compText.height + 4;
      }
    }
    yOffset += 8;

    // Crew section
    const crewHeaderBg = this.scene.add.rectangle(10, yOffset, dw - 20, 26, SECTION_HEADER_COLOR, 1);
    crewHeaderBg.setOrigin(0, 0);
    this.scrollInner.add(crewHeaderBg);

    const crewHeader = this.scene.add.text(20, yOffset + 13, 'CREW', {
      fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
      color: '#44aaff',
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    crewHeader.setOrigin(0, 0.5);
    this.scrollInner.add(crewHeader);
    yOffset += 32;

    if (!crew) {
      const noCrew = this.scene.add.text(25, yOffset, 'No crew assigned', {
        fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
        color: '#556677',
        fontFamily: 'Share Tech Mono, monospace',
      });
      this.scrollInner.add(noCrew);
    } else {
      const crewNameLine = `${crew.name}  (Lv.${crew.level})  -  ${crew.status}`;
      const crewNameText = this.scene.add.text(25, yOffset, crewNameLine, {
        fontSize: `${getResponsiveFontSize(13, this.scene)}px`,
        color: '#ccddff',
        fontFamily: 'Share Tech Mono, monospace',
        fontStyle: 'bold',
      });
      this.scrollInner.add(crewNameText);
      yOffset += 20;

      const barW = Math.min(200, dw * 0.4);
      const skills: { label: string; value: number; color: number }[] = [
        { label: 'Combat', value: crew.combat_skill, color: 0xff4444 },
        { label: 'Piloting', value: crew.piloting_skill, color: 0x44aaff },
        { label: 'Navigation', value: crew.navigation_skill, color: 0x44ff88 },
        { label: 'Engineering', value: crew.engineering_skill, color: 0xffaa44 },
        { label: 'Science', value: crew.science_skill, color: 0xcc66ff },
      ];

      for (const skill of skills) {
        const skillLabel = this.scene.add.text(30, yOffset, `${skill.label}:`, {
          fontSize: `${getResponsiveFontSize(11, this.scene)}px`,
          color: '#8899bb',
          fontFamily: 'Share Tech Mono, monospace',
        });
        this.scrollInner.add(skillLabel);

        const valText = this.scene.add.text(130, yOffset, `${skill.value}`, {
          fontSize: `${getResponsiveFontSize(11, this.scene)}px`,
          color: '#ccddee',
          fontFamily: 'Share Tech Mono, monospace',
        });
        this.scrollInner.add(valText);

        const barX = 160;
        const barH = 8;
        const barBg = this.scene.add.rectangle(barX, yOffset + 2, barW, barH, 0x222233);
        barBg.setOrigin(0, 0);
        this.scrollInner.add(barBg);

        const fillW = Math.max(1, (skill.value / 100) * barW);
        const barFill = this.scene.add.rectangle(barX, yOffset + 2, fillW, barH, skill.color);
        barFill.setOrigin(0, 0);
        this.scrollInner.add(barFill);
        yOffset += 16;
      }
    }
    this.scrollUpdate?.();
  }

  // ================================================================== Shared widgets

  private createSmallButton(x: number, y: number, w: number, h: number, label: string, onClick: () => void): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x, y);
    const bg = this.scene.add.rectangle(0, 0, w, h, ACCENT_COLOR, 0.15);
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(1, ACCENT_COLOR, 0.6);
    btn.add(bg);

    const text = this.scene.add.text(w / 2, h / 2, label, {
      fontSize: `${getResponsiveFontSize(11, this.scene)}px`,
      color: '#44aaff',
      fontFamily: 'Share Tech Mono, monospace',
      fontStyle: 'bold',
    });
    text.setOrigin(0.5);
    btn.add(text);

    btn.setSize(w, h);
    btn.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
    btn.on('pointerover', () => { bg.setFillStyle(ACCENT_COLOR, 0.3); text.setColor('#ffffff'); });
    btn.on('pointerout', () => { bg.setFillStyle(ACCENT_COLOR, 0.15); text.setColor('#44aaff'); });
    btn.on('pointerdown', onClick);
    return btn;
  }
}
